import { installedLiveSources, setInstalledLiveSources } from '../live/NowFutbol.ts';
import { installedAnimeSources, setInstalledAnimeSources } from '../anime/AnimeRepository.ts';
import { sourceProvider } from '../core/SourceProvider.ts';
import { loadKeiyoushiRepository, normalizeRepoUrl } from './KeiyoushiRepoParser.ts';
import { flattenRepository, saveRepositoryEntries, storedRepositoryEntries, type RepositoryEntry } from './RepositoryStore.ts';
import { parseYomiriRepository } from './YomiriRepoParser.ts';
import { getRemoteSnapshot } from '../cast/RemoteSession.ts';
export function readStored<T>(key: string, fallback: T): T {
  const remote=getRemoteSnapshot();
  if(remote&&key==='yomiri_repository_names')return Object.fromEntries(remote.repositories.map(repo=>[repo.url,repo.name]))as T;
  if(remote&&key==='yomiri_repository_catalogs')return Object.fromEntries(remote.repositories.map(repo=>[repo.url,
    (remote.entries||[]).filter(entry=>entry.repository===repo.url).map(entry=>entry.runtimeId)]))as T;
  try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; } catch { return fallback; }
}
export function repositoryIds(): string[] { return getRemoteSnapshot()?.repositories.map(repo=>repo.url) ?? readStored('yomiri_repositories', []); }
export function repositoryKind(id: string): 'manga' | 'anime' { return readStored<Record<string,string>>('yomiri_repository_kinds', {})[id] === 'anime' ? 'anime' : 'manga'; }
function commitRepository(key:string,ids:string[],name:string,kind:string,entries?:RepositoryEntry[]){
  const values:Record<string,string>={
    yomiri_repository_catalogs:JSON.stringify({...readStored('yomiri_repository_catalogs',{}),[key]:ids}),
    yomiri_repository_names:JSON.stringify({...readStored('yomiri_repository_names',{}),[key]:name}),
    yomiri_repository_kinds:JSON.stringify({...readStored('yomiri_repository_kinds',{}),[key]:kind}),
    yomiri_repositories:JSON.stringify([...repositoryIds(),key]),
  };
  if(entries)values.yomiri_external_catalogs=JSON.stringify({...storedRepositoryEntries(),[key]:entries});
  const before=Object.fromEntries(Object.keys(values).map(name=>[name,localStorage.getItem(name)]));
  try{for(const [name,value]of Object.entries(values))localStorage.setItem(name,value);}
  catch{
    for(const [name,value]of Object.entries(before)){try{value===null?localStorage.removeItem(name):localStorage.setItem(name,value);}catch{}}
    throw new Error('No se pudo guardar el repositorio. Revisá el espacio y los permisos de almacenamiento del navegador.');
  }
  window.dispatchEvent(new Event('yomiri-sources-changed'));
}
export async function importRepositoryFile(file: Pick<File, 'size' | 'text'>): Promise<void> {
  if(getRemoteSnapshot())throw new Error('Administrá los repositorios desde el móvil vinculado.');
  if (file.size > 16 * 1024 * 1024) throw new Error('El repositorio supera 16 MiB.');
  const repo = parseYomiriRepository(JSON.parse(await file.text()));
  const key = `file:${encodeURIComponent(repo.name.toLowerCase())}`;
  if (repositoryIds().includes(key)) throw new Error('Este repositorio ya está importado.');
  const entries = await flattenRepository({ ...repo, format: 'yomiri', url: key, baseUrl: key }, key);
  commitRepository(key,entries.map(entry=>entry.runtimeId),repo.name,'manga',entries);
}
export function animeConnector(pkg: string, name: string): string | undefined {
  const key = (pkg + ' ' + name).toLowerCase();
return key.includes('anizone') ? 'anizone' : key.includes('katanime') ? 'katanime' : key.includes('animeflvor') ? 'animeflvor' : key.includes('animeyt') ? 'animeyt' : key.includes('monoschinos') ? 'monoschinos' : key.includes('animeav1') ? 'animeav1' : key.includes('animeflv') ? 'animeflv' : key.includes('jkanime') ? 'jkanime' : undefined;
}
export interface RepositoryPreview {
  url: string;
  key: string;
  name: string;
  kind: 'manga' | 'anime';
  format: 'protobuf' | 'json' | 'yomiri';
  totalExtensions: number;
  totalSources: number;
  entries: RepositoryEntry[];
  sampleSources: Array<{
    id: string;
    name: string;
    lang: string;
    iconUrl?: string;
    baseUrl?: string;
    requiresBridge: boolean;
  }>;
  warnings?: string[];
}

export async function validateAndPreviewRepository(input: string, kind?: 'manga' | 'anime'): Promise<RepositoryPreview> {
  if (getRemoteSnapshot()) throw new Error('Agregá el repositorio desde el móvil vinculado.');
  const cleaned = input.trim().replace(/[\u200B-\u200D\uFEFF]/g, '');
  if (!cleaned) throw new Error('Ingresá la URL del repositorio.');
  const target = cleaned.startsWith('/') && !cleaned.startsWith('//')
    ? new URL(cleaned, location.origin)
    : new URL(/^https?:\/\//i.test(cleaned) ? cleaned : normalizeRepoUrl(cleaned).url);
  if (target.username || target.password || (target.protocol !== 'https:' && target.origin !== location.origin))
    throw new Error('Usá una URL HTTPS sin credenciales.');
  target.hash = '';
  const external = target.origin !== location.origin;
  const key = external ? normalizeRepoUrl(target.href).url : target.pathname;
  if (repositoryIds().includes(key)) throw new Error('Este repositorio ya está agregado.');

  let name: string;
  let detectedKind: 'manga' | 'anime' = kind || 'manga';
  let format: 'protobuf' | 'json' | 'yomiri' = 'json';
  let entries: RepositoryEntry[];
  let totalExtensions = 0;

  try {
    if (external) {
      const repo = await loadKeiyoushiRepository(target.href);
      format = repo.format === 'protobuf' ? 'protobuf' : 'json';
      totalExtensions = repo.extensions.length;
      detectedKind = kind || (repo.extensions.some(extension => /aniyomi|anime/.test(extension.pkg)) || /yuzono|anime-repo|anime-extensions/.test(target.href) ? 'anime' : 'manga');
      entries = await flattenRepository(repo, key);
      name = repo.name;
    } else {
      const response = await fetch(target.href, { signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('No se pudo cargar el repositorio.');
      const data = await response.json() as { format?: string; name?: string; sources?: { id: string }[] };
      if (data.format === 'yomiri.catalog.v1' || data.format === 'yomiri.repository.v1') {
        format = 'yomiri';
        const parsed = parseYomiriRepository(data);
        entries = await flattenRepository({ ...parsed, format: 'yomiri', url: key, baseUrl: target.href }, key);
        totalExtensions = parsed.extensions.length;
        name = parsed.name;
      } else {
        const known = new Set(sourceProvider.getAllExtensions().map(source => source.id));
        if (data.format !== 'yomiri.sources.v1' || !Array.isArray(data.sources) || data.sources.some(source => !known.has(source.id)))
          throw new Error('El repositorio no tiene un formato compatible.');
        format = 'json';
        const ids = data.sources.map(source => source.id);
        name = String(data.name || target.hostname);
        totalExtensions = ids.length;
        entries = ids.map(id => ({
          entryId: id,
          runtimeId: id,
          repository: key,
          extension: { name: id, pkg: id, apk: '', lang: 'all', code: 1, version: '1.0', nsfw: false, sources: [], iconUrl: '' },
          source: { id, name: id, lang: 'all', baseUrl: '' },
          nativeId: id,
        }));
      }
    }
  } catch (err: any) {
    // Zero partial installation: no state is modified on error
    throw new Error(`Manifiesto de repositorio inválido: ${err?.message || err}`);
  }

  const sampleSources = entries.slice(0, 20).map(e => ({
    id: e.runtimeId,
    name: e.source.name,
    lang: e.source.lang,
    iconUrl: e.extension.iconUrl,
    baseUrl: e.source.baseUrl,
    requiresBridge: !e.nativeId,
  }));

  return {
    url: target.href,
    key,
    name,
    kind: detectedKind,
    format,
    totalExtensions,
    totalSources: entries.length,
    entries,
    sampleSources,
  };
}

export function commitPreviewedRepository(preview: RepositoryPreview): void {
  const ids = preview.entries.map(entry => entry.runtimeId);
  commitRepository(preview.key, ids, preview.name, preview.kind, preview.entries);
}

export async function addRepository(input: string, kind?: 'manga' | 'anime'): Promise<void> {
  const preview = await validateAndPreviewRepository(input, kind);
  commitPreviewedRepository(preview);
}
export async function removeRepository(id: string): Promise<void> {
  if(getRemoteSnapshot())throw new Error('Quitá el repositorio desde el móvil vinculado.');
  const entries = storedRepositoryEntries();
  const candidateIds = entries[id]?.map(entry => entry.runtimeId) || readStored<Record<string,string[]>>('yomiri_repository_catalogs', {})[id] || [];
  const others = repositoryIds().filter(value => value !== id);
  {
    const removed = new Set((entries[id] || []).map(entry => animeConnector(entry.extension.pkg, entry.source.name)).filter(Boolean));
    const kept = new Set(others.flatMap(key => (entries[key] || []).map(entry => animeConnector(entry.extension.pkg, entry.source.name))).filter(Boolean));
    if (removed.size) await setInstalledAnimeSources((await installedAnimeSources()).filter(source => !removed.has(source) || kept.has(source)));
    const liveRemoved = new Set((entries[id] || []).filter(entry => entry.source.contentKind === 'live').map(entry => entry.source.connectorId));
    const liveKept = new Set(others.flatMap(key => (entries[key] || []).filter(entry => entry.source.contentKind === 'live').map(entry => entry.source.connectorId)));
    if (liveRemoved.size) await setInstalledLiveSources((await installedLiveSources()).filter(source => !liveRemoved.has(source) || liveKept.has(source)));
  }
  const stillAvailable = new Set(others.flatMap(value => entries[value]?.map(entry => entry.runtimeId) || readStored<Record<string,string[]>>('yomiri_repository_catalogs', {})[value] || []));
  candidateIds.filter(source => !stillAvailable.has(source)).forEach(source => sourceProvider.uninstallSource(source));
  sourceProvider.removeRuntimeRepository(id);
  delete entries[id]; localStorage.setItem('yomiri_external_catalogs', JSON.stringify(entries));
  localStorage.setItem('yomiri_repositories', JSON.stringify(others));
  if (others.length === 0) { try { localStorage.setItem('yomiri_installed_sources_v2', '[]'); localStorage.setItem('yomiri_maintained_installed', '[]'); await setInstalledAnimeSources([]); await setInstalledLiveSources([]); } catch {} }
  for (const key of ['yomiri_repository_catalogs','yomiri_repository_names','yomiri_repository_kinds']) {
    const values = readStored<Record<string, unknown>>(key, {}); delete values[id]; localStorage.setItem(key, JSON.stringify(values));
  }
  window.dispatchEvent(new Event('yomiri-sources-changed'));
}

export async function refreshRepository(id: string): Promise<void> {
  if(getRemoteSnapshot())throw new Error('Actualizá el repositorio desde el móvil vinculado.');
  if (id.startsWith('file:')) throw new Error('Volvé a importar el archivo para actualizarlo.');
  const repo = await loadKeiyoushiRepository(new URL(id,location.origin).href);
  const entries = await flattenRepository(repo,id);
  saveRepositoryEntries(id,entries);
  localStorage.setItem('yomiri_repository_catalogs',JSON.stringify({...readStored('yomiri_repository_catalogs',{}),[id]:entries.map(entry=>entry.runtimeId)}));
  localStorage.setItem('yomiri_repository_names',JSON.stringify({...readStored('yomiri_repository_names',{}),[id]:repo.name}));
  window.dispatchEvent(new Event('yomiri-sources-changed'));
}
